import { toDollars } from '@/util/currency';
import { calculateForecastAmount } from '@common/forecasting';
import { CashManagementGoal } from '@common/goals';
import { CurrentRateInfo } from '@common/graphql/generated';
import { getAmountRecurrenceStr, getGoalBaseConfig } from '@common/react/goals';
import { dt } from '@common/util/dateTime';
import pluralize from '@common/util/pluralize';
import { capitalize } from '@common/util/strings';
import { isNumber } from '@common/util/types';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getFinalAssumptionsWithoutRisk,
    getGoalForecastStartAmount,
    getIOrWe,
    getTargetDateFromStartDateAndYears,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

export const CASH_MANAGEMENT: GoalConfig<CashManagementGoal> = {
    inputSteps: [],
    getGoalStatementContent: ({
        goalData,
        onGoalStatementClick,
        currentRateInfo,
        userInfo,
        isReadOnlyIfTargetDateIsDynamic,
    }) => {
        const { inNumYearsStr, projectedAmount } = getProjectedAmountData(
            goalData,
            currentRateInfo
        );
        const targetDate = CASH_MANAGEMENT.getTargetDate({
            goalData,
            currentRateInfo,
            userInfo,
        });

        if (
            !isNumber(projectedAmount) ||
            !inNumYearsStr ||
            !goalData.transactionSchedulePlan ||
            !targetDate
        ) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const iOrWe = getIOrWe(goalData);
        const color = CASH_MANAGEMENT.color;

        const hasBalance = isNumber(goalData.balance) && goalData.balance > 0;
        const hasRecurrence =
            goalData.transactionSchedulePlan &&
            goalData.transactionSchedulePlan.amount > 0;
        const projectedAmountStr = `${iOrWe}'ll have an estimated ${toDollars(
            projectedAmount
        )} ${inNumYearsStr}.`;

        // TODO: Revise this to consider both partners

        // If user has existing balance and no recurring transactions,
        // just report the projected amount.
        if (hasBalance && !hasRecurrence) {
            return <>{capitalize(projectedAmountStr)}</>;
        }

        // Otherwise, show the full statement, omitting irrelevant parts as
        // necessary.
        return (
            <>
                {`If ${iOrWe} `}
                {/* If user has existing balance, omit the initial deposit phrase */}
                {!hasBalance && goalData.initialDepositPlan ? (
                    <>
                        {'start with '}
                        <GoalStatementLinkText
                            text={toDollars(goalData.initialDepositPlan)}
                            onClick={() =>
                                onGoalStatementClick('initialDepositPlan')
                            }
                            color={color}
                            isReadOnly={isReadOnlyIfTargetDateIsDynamic}
                        />
                    </>
                ) : null}
                {!hasBalance && !!goalData.initialDepositPlan && hasRecurrence
                    ? ' and '
                    : null}
                {hasRecurrence ? (
                    <>
                        {'contribute '}
                        <GoalStatementLinkText
                            text={getAmountRecurrenceStr(
                                goalData.transactionSchedulePlan
                            )}
                            onClick={() =>
                                onGoalStatementClick('transactionSchedulePlan')
                            }
                            color={color}
                            isReadOnly={isReadOnlyIfTargetDateIsDynamic}
                        />
                    </>
                ) : null}
                {`, ${iOrWe}'ll have a hypothetical ${toDollars(
                    projectedAmount
                )} ${inNumYearsStr}.`}
            </>
        );
    },
    getAssumptionsContent: getFinalAssumptionsWithoutRisk,
    getRecommendedRiskScore: () => 0,
    getTargetDate: () => dt().add(25, 'years').toDateStr(),
    ...getGoalBaseConfig('CASH_MANAGEMENT'),
};

function getProjectedAmountData(
    goalData: GoalData<CashManagementGoal>,
    currentRateInfo: CurrentRateInfo
): {
    projectedAmount?: number;
    inNumYears?: number;
    inNumYearsStr?: string;
} {
    // FIXME: This is broken for shared goals.
    const {
        balance,
        initialDepositPlan: initialDepositPlan,
        transactionSchedulePlan: transactionSchedulePlan,
    } = goalData;

    const inNumYears: number = 1;
    const inNumYearsStr = `in ${pluralize('year', inNumYears, true)}`;

    const startDate = dt().toDateStr(); // TODO: Reconsider this assumption
    const targetDate = getTargetDateFromStartDateAndYears(
        startDate,
        inNumYears
    );
    const projectedAmount =
        transactionSchedulePlan && targetDate
            ? calculateForecastAmount({
                  transactionSchedulePlans: [transactionSchedulePlan],
                  annualGrowthRate: currentRateInfo.moneyMarketRate,
                  startAmount: getGoalForecastStartAmount(
                      balance,
                      initialDepositPlan
                  ),
                  startDate,
                  targetDate,
              })
            : undefined;

    return {
        inNumYears,
        inNumYearsStr,
        projectedAmount,
    };
}
